import { useState, useCallback } from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Frame, Navigation, TopBar } from '@shopify/polaris';
import {
  HomeIcon,
  OrderIcon,
  PersonIcon,
  ChartVerticalIcon,
  ReturnIcon,
  ExitIcon,
  SettingsIcon,
} from '@shopify/polaris-icons';
import { useClinicAuth } from '../../contexts/ClinicAuthContext';

export default function ClinicLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, user, logout } = useClinicAuth();
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const toggleMobileNavigation = useCallback(() => {
    setMobileNavigationActive((active) => !active);
  }, []);

  const toggleUserMenu = useCallback(() => {
    setUserMenuOpen((open) => !open);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/clinic/login');
  }, [logout, navigate]);

  // Show nothing while loading auth state
  if (isLoading) {
    return null;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/clinic/login" replace />;
  }

  const navigationMarkup = (
    <Navigation location={location.pathname}>
      <Navigation.Section
        items={[
          {
            label: 'Dashboard',
            icon: HomeIcon,
            onClick: () => navigate('/clinic'),
            selected: location.pathname === '/clinic',
          },
        ]}
      />
      <Navigation.Section
        title="My Clinic"
        items={[
          {
            label: 'Orders',
            icon: OrderIcon,
            onClick: () => navigate('/clinic/orders'),
            selected: location.pathname.startsWith('/clinic/orders'),
          },
          {
            label: 'Patients',
            icon: PersonIcon,
            onClick: () => navigate('/clinic/patients'),
            selected: location.pathname.startsWith('/clinic/patients'),
          },
          {
            label: 'Doctors',
            icon: PersonIcon,
            onClick: () => navigate('/clinic/doctors'),
            selected: location.pathname.startsWith('/clinic/doctors'),
          },
        ]}
      />
      <Navigation.Section
        title="Actions"
        items={[
          {
            label: 'Reports',
            icon: ChartVerticalIcon,
            onClick: () => navigate('/clinic/reports'),
            selected: location.pathname.startsWith('/clinic/reports'),
            subNavigationItems: [
              { label: 'Summary', url: '/clinic/reports' },
              { label: 'Not Returned', url: '/clinic/reports/not-returned' },
              { label: 'Overdue', url: '/clinic/reports/overdue' },
              { label: 'Tomorrow Overdue', url: '/clinic/reports/tomorrow-overdue' },
            ],
          },
          {
            label: 'Request Return',
            icon: ReturnIcon,
            onClick: () => navigate('/clinic/return-request'),
            selected: location.pathname.startsWith('/clinic/return-request'),
          },
        ]}
      />
      <Navigation.Section
        title="Settings"
        items={[
          {
            label: 'Profile',
            icon: SettingsIcon,
            onClick: () => navigate('/clinic/profile'),
            selected: location.pathname === '/clinic/profile',
          },
          {
            label: 'Sign Out',
            icon: ExitIcon,
            onClick: handleLogout,
          },
        ]}
      />
    </Navigation>
  );

  const userName = user?.name || user?.email || '';
  const userMenuMarkup = user ? (
    <TopBar.UserMenu
      actions={[
        {
          items: [
            {
              content: 'Sign out',
              onAction: handleLogout,
            },
          ],
        },
      ]}
      name={userName}
      detail={user.clinicName}
      initials={userName.charAt(0).toUpperCase()}
      open={userMenuOpen}
      onToggle={toggleUserMenu}
    />
  ) : undefined;

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      onNavigationToggle={toggleMobileNavigation}
      userMenu={userMenuMarkup}
    />
  );

  return (
    <Frame
      topBar={topBarMarkup}
      navigation={navigationMarkup}
      showMobileNavigation={mobileNavigationActive}
      onNavigationDismiss={toggleMobileNavigation}
    >
      <Outlet />
    </Frame>
  );
}
